import {clearAfterCreateContractEntities, clientPassportType, getSystemEntityById, SystemEntity, contractNumber, currentDate} from "./SystemEntity";
import {getPostfix, getPostfixForEntity} from "./utils";
import {updateEntityInEditor} from "./updateEntityInEditor";
import {entityById} from "./systemEntityValueByKeyword";
import _ from "lodash";
import {arrayMove} from "./arrayMove";
import {$moment} from "../funcs";
import { getEntityPatterns } from "./emptyEntities";
import { getParagraphFullText } from "./workWithParagraphEntity";
import { checkInlineEntities } from "./checkInlineEntities";
import { applyObjectInnerValuesToMatchingFields } from "./workWithObjectEntity";
import {
    BOOL_ENTITY_TYPE,
    boolEntityTextValue,
    boolEntityContractTextValue,
    boolEntityOmitsContractText,
    boolEntityToValue,
    isBoolEntityChecked,
    isBoolEntityEmpty,
    boolEntityRequiresChecked,
} from "./boolEntityField";

export {
    BOOL_ENTITY_TYPE,
    isBoolEntityChecked,
    boolEntityToValue,
    boolEntityTextValue,
    boolEntityContractTextValue,
    boolEntityOmitsContractText,
    isBoolEntityEmpty,
    boolEntityRequiresChecked,
};

export function getEntitySpanInsertValue(entity: any, otherEntities: any[] = []): string {
    if (entity?.type === "PDF") {
        return "";
    }
    const value = entity?.type === BOOL_ENTITY_TYPE
        ? boolEntityContractTextValue(entity)
        : fullEntityValue(entity, "\n", otherEntities);
    if (value != null && value.trim() !== "") {
        return value;
    }
    if (entity?.type === BOOL_ENTITY_TYPE && boolEntityOmitsContractText(entity)) {
        return "";
    }
    // hidden = «не спрашивать на форме», не «убрать метку из текста».
    // ФИО клиента всегда hidden (computed), иначе чип {{ФИО клиента}} сразу пустеет.
    // Пустые необязательные поля в редакторе шаблона показывают {{метку}}.
    // Скрытие пустых меток — на бэке при выставлении/подписании договора.
    const keyword = entity?.keyword;
    if (!keyword) {
        return "";
    }
    return getEntityPatterns(keyword).rawPattern;
}

/**
 * Опции поля «Список» в БД иногда приходят строкой через `;`
 * (ИИ / легаси), а UI ожидает string[].
 */
export function normalizeEntityList(list: unknown): string[] {
    if (Array.isArray(list)) {
        return list.map((item) => (item == null ? '' : String(item)));
    }
    if (typeof list === 'string') {
        return list
            .split(';')
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
    }
    return [];
}

export function cleanKeyword(text: string): string {
    if (!text) return '';
    return text.trim().replace(/[<>"'&]/g, '');
}

export function cleanKeywordWithoutTrim(text: string): string {
    if (!text) return '';
    return text.replace(/[<>"'&]/g, '');
}

export function updateEntityInList(entities: any[], keyword: string, value: string): any[] {
    for (const entity of entities) {
        if (entity.keyword == keyword) {
            entity.value = value
        }
    }
    return entities
}

export function entityByKeyword(entities: any[], keyword: string, oldKeyword: string | null = null): any | undefined {
    if (keyword.length == 0) {
        return
    }
    for (const entity of entities) {
        if (entity.keyword == keyword || entity.keyword == oldKeyword) {
            return entity
        }
    }
}

export function rawEntityValue(entity: any): string {
    if (entity.type === "Таблица") {
        return generateTableHtml(entity)
    }
    
    let value = entity.value
    if (!value) {
        return ""
    }
    let stringValue = value.toString().trim()
    return stringValue
}

export function fullEntityValue(entity: any, separator: string = "\n", otherEntities: any[] = [], addPostfix: boolean = true): string {
    if (!entity) {
        return ""
    }

    let type = entity.type
    let value = rawEntityValue(entity)
    let keyword = entity.keyword ?? ""
    let {normalPattern, escapedPattern, rawPattern} = getEntityPatterns(keyword)

    // Специальная обработка для таблиц (до проверки на пустое значение)
    if (type === "Таблица") {
        return generateTableHtml(entity)
    }

    if (type === BOOL_ENTITY_TYPE) {
        return boolEntityTextValue(entity)
    }

    // Если значение пустое (включая только пробелы), возвращаем пустую строку
    if (!value || value.trim() === "") {
        return ""
    }

    if (type === "Абзац") {
        return getParagraphFullText(entity);
    }

    if (entity.type === "Объект") {
        return value
    }

    if (type === "Фото") {
        return value + additionalValuesString(entity, separator)
    }

    if (type === "Дата") {
        return value
    }

    let result = value
    if (addPostfix) {
        let postfix = getPostfix(value, entity.needNumberToWords ?? false, entity.currency)
        let additionalValues = additionalValuesString(entity, separator)
        
        result = value + postfix + additionalValues
    } 
    
    if (keyword && otherEntities.length > 0 && (type === "Текст" || type === "Список")) {
        result = checkInlineEntities(result, rawPattern, otherEntities)
    }
    
    return result
}

export function generateTableHtml(entity: any): string {
    const tableData = entity?.table || {}
    const titles: any[] = Array.isArray(tableData.titles) ? tableData.titles : []
    const rows: any[][] = Array.isArray(tableData.rows) ? tableData.rows : []

    if (!titles.length || !rows.length) {
        return ""
    }

    let html = '<table style="width: 100%; border-collapse: collapse; margin: 10px 0;">\n'

    // thead
    html += '  <thead>\n    <tr>\n'
    for (const title of titles) {
        html += `      <th style="border: 1px solid #ddd; padding: 8px; text-align: left; background-color: #f2f2f2; font-weight: bold;">${title}</th>\n`
    }
    html += '    </tr>\n  </thead>\n'

    // tbody
    html += '  <tbody>\n'
    for (const row of rows) {
        html += '    <tr>\n'
        for (let i = 0; i < row.length; i++) {
            if (i < titles.length) {
                html += `      <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${row[i]}</td>\n`
            }
        }
        html += '    </tr>\n'
    }
    html += '  </tbody>\n'

    html += '</table>'

    return html
}

export function additionalValuesString(entity: any, separator: string = "\n"): string {
    if (!entity) {
        return ""
    }
    let result = ""
    if (entity.allowMultipleValues ?? false) {
        for (let additionalValue of entity.additionalValues ?? []) {
            if (additionalValue.length > 0) {
                let postfix = entity.postfix ? getPostfixForEntity({...entity, value: additionalValue}) : ''
                result += separator + additionalValue + postfix
            }
        }
    }
    return result
}

/** Настройки полей слота доп. подписанта без value клиента (Kaiten 69053549). */
export function stripSignerSlotPrefillValues(entities: any[]): any[] {
    return (entities ?? []).map((entity) => {
        const def = clearAfterCreateContractEntities.find(
            (e) => e.id === entity.id || e.keyword === entity.keyword,
        )
        if (!def) {
            return {...entity}
        }
        const copy = {...entity}
        if (copy.id === clientPassportType.id) {
            copy.value = copy.selectedValues?.[0] ?? ''
        } else {
            copy.value = ''
            copy.postfix = ''
        }
        if (copy.type === 'Дата') {
            copy.raw_value = undefined
            copy.isManualDate = false
        }
        return copy
    })
}

function slotEntityMatches(slotEntity: any, entity: any): boolean {
    const entityId = entity?.id
    const keyword = entity?.keyword
    return !!(
        (entityId && (slotEntity?.id === entityId || slotEntity?.keyword === entityId))
        || (keyword && (slotEntity?.keyword === keyword || slotEntity?.id === keyword))
    )
}

function slotEntityIndex(slotEntities: any[], entity: any): number {
    return (slotEntities ?? []).findIndex((slotEntity: any) => slotEntityMatches(slotEntity, entity))
}

function slotEntityLooksUnused(slotEntity: any): boolean {
    if (slotEntity?.hidden !== true) {
        return false
    }
    return !String(slotEntity?.value ?? '').trim()
}

/**
 * Слот доп. подписанта — снимок system_entities.
 * Нового поля может не быть, либо оно лежит с catalog default hidden:true и пустым value
 * (включили галочку только у «Клиента»). Берём hidden/editable с клиента, value не копируем.
 */
export function inheritMissingSystemEntitiesFromClient(
    slotEntities: any[] | undefined,
    clientEntities: any[] | undefined,
): any[] {
    const merged = [...(slotEntities ?? [])]
    const skipIds = [contractNumber.id, currentDate.id]
    const skipKeywords = [contractNumber.keyword, currentDate.keyword]
    for (const entity of clientEntities ?? []) {
        if (!entity || skipIds.includes(entity.id) || skipKeywords.includes(entity.keyword)) {
            continue
        }
        const existingIndex = slotEntityIndex(merged, entity)
        if (existingIndex >= 0) {
            const slotEntity = merged[existingIndex]
            if (slotEntityLooksUnused(slotEntity) && entity.hidden === false) {
                merged[existingIndex] = {
                    ...slotEntity,
                    hidden: false,
                    editable: entity.editable ?? slotEntity.editable,
                }
            }
            continue
        }
        merged.push(stripSignerSlotPrefillValues([{...entity}])[0])
    }
    return merged
}

/** Пустое value подставляем из defaultValue — иначе после дубля адрес «пропадает» с формы. */
export function fillEntitiesWithDefaultValue(entities: any[] | undefined): any[] {
    if (!Array.isArray(entities)) {
        return []
    }
    return entities.map((entity: any) => {
        const value = entity?.value
        const defaultValue = entity?.defaultValue
        if (entity?.type === BOOL_ENTITY_TYPE) {
            if (value === undefined || value === null || value === "") {
                return {
                    ...entity,
                    value: isBoolEntityChecked(defaultValue) ? "true" : "false",
                }
            }
            return entity
        }
        if ((value === undefined || value === null || value === "") && defaultValue) {
            return { ...entity, value: defaultValue }
        }
        return entity
    })
}

/** Пустой ввод в поле со значением по умолчанию снова подставляет defaultValue. */
export function valueOrEntityDefault(entity: { defaultValue?: unknown } | null | undefined, raw: unknown): string {
    const value = raw == null ? "" : String(raw)
    if (value.trim() !== "") {
        return value
    }
    const fallback = entity?.defaultValue
    if (fallback == null || String(fallback).trim() === "") {
        return ""
    }
    return String(fallback)
}

export function clearedEntitiesTemplate(template: any) {
    let entities = [...template.entities]
    let system_entities = [...template.system_entities]
    for (let entity of entities) {
        if (!["Объект", "Абзац"].includes(entity.type)) {
            if (entity.type === BOOL_ENTITY_TYPE) {
                entity.value = isBoolEntityChecked(entity.defaultValue) ? "true" : "false"
            } else {
                entity.value = entity.defaultValue ?? ""
            }
        }
        entity.additionalValues = []
        entity.postfix = ""
        if (entity.type === "Дата") {
            entity.raw_value = undefined
            entity.isManualDate = false
        }
        updateEntityInEditor(entity)
    }

    for (let entity of clearAfterCreateContractEntities) {
        let entityToClear = entityById(system_entities, entity.id)
        if (entityToClear) {
            if (entityToClear.id == clientPassportType.id) {
                entityToClear.value = entityToClear.selectedValues[0]
            } else {
                entityToClear.value = ""
                entityToClear.postfix = ""
            }

            if (entityToClear.type === "Дата") {
                entityToClear.isManualDate = false
                entityToClear.raw_value = undefined
            }
        }
    }

    // Сбрасываем флаг overrideIncrement для contractNumber
    let contractNumberEntity = entityById(system_entities, contractNumber.id)
    if (contractNumberEntity) {
        contractNumberEntity.overrideIncrement = false
    }

    // Сбрасываем URL для одноразовой ссылки
    if (template.settings?.payment_url?.type === "single") {
        template.settings = {
            ...template.settings,
            payment_url: {
                ...template.settings.payment_url,
                url: ""
            }
        }
    }

    if (template.settings?.first_signer_prefill) {
        template.settings = {
            ...template.settings,
            first_signer_prefill: [],
        }
    }

    // Очищаем данные (ФИО, паспорт и т.д.) для additional_signers
    const rawSigners = template.settings?.additional_signers ?? []
    const additionalSigners = Array.isArray(rawSigners) ? rawSigners : []
    for (const slot of additionalSigners) {
        const slotEntities = slot.system_entities ?? []
        for (const entity of clearAfterCreateContractEntities) {
            const entityToClear = entityById(slotEntities, entity.id)
            if (entityToClear) {
                if (entityToClear.id == clientPassportType.id) {
                    entityToClear.value = entityToClear.selectedValues?.[0] ?? ""
                } else {
                    entityToClear.value = ""
                    entityToClear.postfix = ""
                }
                if (entityToClear.id === currentDate.id) {
                    entityToClear.isManualDate = false
                    entityToClear.raw_value = undefined
                }
            }
        }
    }

    return {
        ...template,
        entities: entities,
        system_entities: system_entities
    }
}


export function getEntities(contract: any, isSystem: boolean): any[] {
    let path = isSystem ? 'system_entities' : 'entities'

    return [..._.get(contract, path, [])]
}

export function updateEntities(contract: any, isSystem: boolean, any_entities: any[], refreshOnly?: any, innerKeyword?: string): any {
    let path = isSystem ? 'system_entities' : 'entities'

    let updatedContract = {...contract}
    _.set(updatedContract, path, any_entities)

    const refresh = () => {
        const list = refreshOnly ? [refreshOnly] : any_entities
        for (let entity of list) {
            updateEntityInEditor(entity, isSystem ? [] : updatedContract.entities, undefined, innerKeyword)
        }
    }
    // Сразу, пока React не перезаписал редактор всем body.
    refresh()
    if (!refreshOnly) {
        setTimeout(refresh, 100)
    }

    return updatedContract
}

export function updateEntity(contract: any, isSystem: boolean, entity: any, innerKeyword?: string): any {
    let any_entities = getEntities(contract, isSystem)
    const entityIndex = any_entities.findIndex(
        (e: any) => e.keyword === entity.keyword,
    );
    any_entities[entityIndex] = entity
    if (!isSystem && entity?.type === "Объект") {
        any_entities = applyObjectInnerValuesToMatchingFields(any_entities, entity)
    }
    return updateEntities(contract, isSystem, any_entities, entity, innerKeyword)
}

export function addEntities(contract: any, isSystem: boolean, entities: any[], innerKeyword?: string): any {
    let any_entities = getEntities(contract, isSystem)
    for (const entity of entities) {
        const cleanedKeyword = cleanKeyword(entity.keyword);
        entity.keyword = cleanedKeyword;
        
        const entityIndex = any_entities.findIndex(
            (e: any) => e.keyword === cleanedKeyword,
        );
        if (entityIndex != -1) {
            any_entities[entityIndex] = entity
        } else {
            any_entities.push(entity)
        }
        // console.log("addEntities", entity)
    }
    return updateEntities(contract, isSystem, any_entities, undefined, innerKeyword)
}

export function deleteEntities(contract: any, isSystem: boolean, keywords: string[]): any {
    let any_entities = getEntities(contract, isSystem)
    const cleanedKeywords = keywords.map(k => cleanKeyword(k));
    
    if (isSystem) {
        for (let any_entity of any_entities) {
            for (let keyword of keywords) {
                if (keyword == any_entity.keyword) {
                    any_entity.hidden = true
                }
            }
        }
    } else {
        any_entities = any_entities.filter((entity: any) => !keywords.includes(entity.keyword))
    }

    return updateEntities(contract, isSystem, any_entities)
}
export function renameEntity(contract: any, oldKeyword: string, newKeyword: string): any {
    if (!oldKeyword || oldKeyword.length == 0) {
        return
    }
    
    const cleanedKeyword = cleanKeyword(newKeyword);
    
    let entities = [...contract.entities]
    
    for (let index = 0; index < entities.length; index++) {
        let entity = entities[index]
        
        // Проверяем, является ли oldKeyword внутренним полем объекта
        const isInnerField = oldKeyword.includes('.') && entity.keyword === oldKeyword.split('.')[0];
        
        if (entity.keyword == oldKeyword) {
            entity.keyword = cleanedKeyword
            let {normalPattern, escapedPattern, rawPattern} = getEntityPatterns(oldKeyword)
            let {normalPattern: newNormalPattern, escapedPattern: newEscapedPattern, rawPattern: newRawPattern} = getEntityPatterns(cleanedKeyword)
            
            contract.body = contract.body.replace(rawPattern, newRawPattern)
            contract.body = contract.body.replace(normalPattern, newNormalPattern)
            contract.body = contract.body.replace(escapedPattern, newEscapedPattern)

            // Обработка внутренних полей абзаца
            if (entity.type === "Абзац" && entity.values) {
                entity.values = entity.values.map((paragraph: any) => {
                    // Обновляем текст абзаца
                    if (paragraph.text) {
                        paragraph.text = paragraph.text.replace(rawPattern, newRawPattern)
                            .replace(normalPattern, newNormalPattern)
                            .replace(escapedPattern, newEscapedPattern)
                    }

                    // Обновляем формулы во внутренних полях
                    if (paragraph.entities) {
                        paragraph.entities = paragraph.entities.map((innerEntity: any) => {
                            if (innerEntity.type === "Формула" && innerEntity.formula) {
                                innerEntity.formula = innerEntity.formula.replace(oldKeyword, cleanedKeyword)
                            }
                            return innerEntity
                        })
                    }
                    return paragraph
                })
            }

            // Обработка внутренних полей объекта
            if (entity.type === "Объект" && entity.values) {
                entity.values = entity.values.map((objectValue: any, valueIndex: number) => {
                    // Обновляем внутренние поля объекта
                    if (objectValue.entities) {
                        objectValue.entities = objectValue.entities.map((innerEntity: any, innerIndex: number) => {
                            if (innerEntity.type === "Формула" && innerEntity.formula) {
                                innerEntity.formula = innerEntity.formula.replace(oldKeyword, cleanedKeyword)
                            }
                            return innerEntity
                        })
                    }
                    return objectValue
                })
            }
        } else if (isInnerField) {
            // Обновляем метки в тексте документа для внутреннего поля
            let {normalPattern, escapedPattern, rawPattern} = getEntityPatterns(oldKeyword)
            let {normalPattern: newNormalPattern, escapedPattern: newEscapedPattern, rawPattern: newRawPattern} = getEntityPatterns(cleanedKeyword)
            
            contract.body = contract.body.replace(rawPattern, newRawPattern)
            contract.body = contract.body.replace(normalPattern, newNormalPattern)
            contract.body = contract.body.replace(escapedPattern, newEscapedPattern)
            
            // Обновляем keyword во внутренних полях объекта
            if (entity.type === "Объект" && entity.values) {
                const innerFieldName = oldKeyword.split('.')[1];
                const newInnerFieldName = cleanedKeyword.split('.')[1];
                
                entity.values = entity.values.map((objectValue: any, valueIndex: number) => {
                    if (objectValue.entities) {
                        objectValue.entities = objectValue.entities.map((innerEntity: any, innerIndex: number) => {
                            if (innerEntity.keyword === innerFieldName) {
                                innerEntity.keyword = newInnerFieldName;
                            }
                            return innerEntity
                        })
                    }
                    return objectValue
                })
            }
        } else {
            if (entity.type == "Формула" && entity.formula.includes(oldKeyword)) {
                entity.formula = entity.formula.replace(oldKeyword, cleanedKeyword)
            }
        }
        entities[index] = entity
    }
    
    return updateEntities(contract, false, entities);
}

export function setOrderForEntity(contract: any, keyword: string, order: number): any {
    let entities = [...contract.entities]
    let oldIndex = entities.findIndex((entity: any) => entity.keyword === keyword)
    if (oldIndex >= 0) {
        arrayMove(entities, oldIndex, order)

        let updatedContract = {...contract}
        updatedContract.entities = entities
        return updatedContract
    } else {
        return contract
    }
}

export function passportIssuedAtTitle(issuedAt: any): string {
    if (!issuedAt) {
        return ""
    }
    // В структуре user.passport ожидаем ISO дату
    let date = $moment(issuedAt, "YYYY-MM-DD")
    if (!date.isValid()) {
        date = $moment(issuedAt, "DD.MM.YYYY")
    }

    if (date.isValid()) {
        return date.format("DD.MM.YYYY")
    } else {
        return issuedAt.toString()
    }
}

export function sortArrayByOrder(array: any[]): any[] {
    return array.sort((a: any, b: any) => {
        if (a.order > b.order) {
            return 1
        } else if (a.order < b.order) {
            return -1
        }
        return 0
    })
}